import {
	DEFAULT_SIDE_PANEL_WIDTH,
	DetailsMainPanel,
	DetailsPanelsContainer,
	DetailsPanelsFooter,
	DetailsPanelsRow,
	DetailsSidePanelContainer,
} from '@dealstack/frontend/shared/layout/DetailsPanels/DetailsPanels.styles.tsx';
import { PropsWithChildren, ReactNode } from 'react';

interface Props {
	/** Rendered before the main region, e.g. a list of the records being reviewed. */
	leadingPanel?: ReactNode;
	/** Rendered after the main region, e.g. a tabbed review/history panel. */
	sidePanel?: ReactNode;
	/** Spans the full width below both panels, e.g. prev/next navigation. */
	footer?: ReactNode;
	leadingPanelWidth?: string;
	sidePanelWidth?: string;
	withBorder?: boolean;
	minWidth?: string;
	dataTestId?: string;
}

/**
 * Detail-page shell: an optional leading panel, a main region that takes the remaining
 * width, an optional side panel, and an optional footer beneath them. Purely
 * presentational — every slot is supplied by the page.
 */
export const DetailsPanels = ({
	leadingPanel,
	sidePanel,
	footer,
	leadingPanelWidth = DEFAULT_SIDE_PANEL_WIDTH,
	sidePanelWidth = DEFAULT_SIDE_PANEL_WIDTH,
	withBorder = true,
	minWidth,
	dataTestId,
	children,
}: PropsWithChildren<Props>) => (
	<DetailsPanelsContainer $withBorder={withBorder} $minWidth={minWidth} data-testid={dataTestId}>
		<DetailsPanelsRow>
			{leadingPanel && (
				<DetailsSidePanelContainer $width={leadingPanelWidth} $side="leading">
					{leadingPanel}
				</DetailsSidePanelContainer>
			)}

			<DetailsMainPanel>{children}</DetailsMainPanel>

			{sidePanel && (
				<DetailsSidePanelContainer $width={sidePanelWidth} $side="trailing">
					{sidePanel}
				</DetailsSidePanelContainer>
			)}
		</DetailsPanelsRow>

		{footer && <DetailsPanelsFooter>{footer}</DetailsPanelsFooter>}
	</DetailsPanelsContainer>
);
